import type { Category, TerminalPolarity } from "./types";

export type CategoryPalette = { fg: string; bg: string; ring: string };

// bg / ring are the foreground hue at higher lightness. Move a hue in
// src/index.css and move it here too.
export const CATEGORY_COLORS: Record<Category, CategoryPalette> = {
  battery:      { fg: "var(--color-cat-battery)",      bg: "hsl(150 60% 96%)", ring: "hsl(150 60% 88%)" },
  charger:      { fg: "var(--color-cat-charger)",      bg: "hsl(214 88% 96%)", ring: "hsl(214 88% 88%)" },
  solar:        { fg: "var(--color-cat-solar)",        bg: "hsl(48 90% 95%)",  ring: "hsl(48 90% 84%)" },
  source:       { fg: "var(--color-cat-source)",       bg: "hsl(78 50% 95%)",  ring: "hsl(78 50% 84%)" },
  engine:       { fg: "var(--color-cat-engine)",       bg: "hsl(30 50% 95%)",  ring: "hsl(30 50% 86%)" },
  distribution: { fg: "var(--color-cat-distribution)", bg: "hsl(265 65% 96%)", ring: "hsl(265 65% 88%)" },
  load:         { fg: "var(--color-cat-load)",         bg: "hsl(322 60% 96%)", ring: "hsl(322 60% 86%)" },
  monitor:      { fg: "var(--color-cat-monitor)",      bg: "hsl(190 70% 95%)", ring: "hsl(190 70% 86%)" },
};

const FALLBACK_COLORS: CategoryPalette = {
  fg: "hsl(220 8% 45%)",
  bg: "hsl(220 8% 96%)",
  ring: "hsl(220 8% 86%)",
};

/** The `Record<Category, …>` looks total to TypeScript, but a saved file can
 *  carry a retired category and hand the renderer `undefined`. */
export function categoryColors(category: string | undefined): CategoryPalette {
  if (category && Object.prototype.hasOwnProperty.call(CATEGORY_COLORS, category)) {
    return CATEGORY_COLORS[category as Category];
  }
  return FALLBACK_COLORS;
}

// `neutral` covers polarity-agnostic terminals (a/b on a generic switch, AC L/N).
export const TERMINAL_COLORS: Record<TerminalPolarity, string> = {
  positive: "hsl(0 60% 48%)",
  negative: "hsl(220 8% 30%)",
  ground:   "hsl(120 30% 35%)",
  signal:   "hsl(265 65% 55%)",
  neutral:  "hsl(220 8% 60%)",
};

// Every preset stays visibly clear of the polarity colours above — no red, no
// charcoal — so a data run never reads as a power rail.
export const SIGNAL_COLOR_PRESETS = [
  "#e6b800", // amber / yellow
  "#e07b00", // orange
  "#3a9856", // moss green
  "#0aa1b0", // teal
  "#1e7fd1", // blue
  "#7a3fb3", // violet
  "#c44b9a", // magenta
  "#7a5230", // brown
] as const;

export type SignalColor = (typeof SIGNAL_COLOR_PRESETS)[number];

export const DEFAULT_SIGNAL_COLOR: SignalColor = SIGNAL_COLOR_PRESETS[4]; // blue
